import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import type { Tarjima } from "../utils/i18n";
import { DAY_FILL_COLORS, type DayCell, type DayFill } from "../utils/summary";

const CELL_W = 4;
const GAP = 1;
const HEIGHT = 18;

/**
 * ⚠️ Rang xaritasi EMAS — bu FAQAT o'qish uchun matn. Rang
 * `DAY_FILL_COLORS` dan (`utils/summary.ts`), takrorlanmaydi. Matn
 * `employees.json`ning `dayStrip.fillCompact.*` kalitlaridan.
 */
const IXCHAM_YORLIQ_TARTIBI: DayFill[] = ["ish", "yoq", "uzr", "dam"];

/**
 * Lentaning YAGONA `aria-label` i — holatlarni SO'Z va SANOQ bilan aytadi.
 *
 * ⚠️ **Rang yolg'iz ma'no tashuvchi emas** (`DayStrip.tsx` qoidasining
 * davomi): rangni ajrata olmaydigan foydalanuvchi uchun lenta aks holda
 * ma'nosiz bo'lardi. To'liq lentada har katak o'z tooltip'iga ega; ixcham
 * variantda esa butun lenta uchun bitta matn — sabab quyida.
 */
export function describeCells(cells: DayCell[], t: Tarjima): string {
	const sanoq: Record<DayFill, number> = { ish: 0, yoq: 0, uzr: 0, dam: 0 };

	for (const cell of cells) {
		sanoq[cell.fill] += 1;
	}

	const qismlar = IXCHAM_YORLIQ_TARTIBI.filter((fill) => sanoq[fill] > 0).map(
		(fill) => `${sanoq[fill]} ${t(`dayStrip.fillCompact.${fill}`)}`
	);

	return [t("dayStrip.compactSummary", { soni: cells.length }), ...qismlar].join(" · ");
}

/**
 * Jadval hujayrasi uchun ixcham davomat lentasi.
 *
 * ⚠️⚠️ **Nima uchun `DayStrip` qayta ishlatilmaydi — IKKI o'lchangan sabab:**
 *
 * 1. **Qator balandligi.** `DayStrip` konteyneri `flex flex-wrap gap-1` va
 *    katagi `h-7 w-4` (28×16 px). Jadval hujayrasida 30 katak bir qatorga
 *    sig'maydi va O'RALADI: ustun kengligiga qarab 2–4 qator × 28 px, ya'ni
 *    jadval qatori 3–4 barobar balandlashadi.
 * 2. **Tooltip soni.** `DayStrip` HAR katakni antd `Tooltip` ga o'raydi.
 *    `pageSizeOptions` maksimumi 100 (`shared/components/DataTable.tsx`) →
 *    bitta sahifada 100 × 30 = 3 000 ta Tooltip trigger.
 *
 * Shu sababli bu variant BITTA inline `<svg>` va BITTA hodisa nishoni.
 *
 * ⚠️ **Kechikish/erta ketish chiziqchalari ATAYLAB chizilmaydi.** 4×18 px
 * katakdagi 2 px chiziqcha o'qib bo'lmaydi, yarim ko'rinadigan signal esa
 * signalsizlikdan yomon. Kechikish ma'nosini qo'shni «O'rt. kechikish»
 * ustuni ochiq matn bilan tashiydi.
 *
 * ⚠️ **Barcha `<rect>` bir xil balandlikda.** Kanvasdagi sparkline
 * ustunchalarining balandlik kodlashi (100 % / 70 % / 55 % / 45 % / 20 %)
 * OLINMAYDI: u nimani anglatishi kanvasda izohlanmagan va ma'lumot
 * modelida unga mos o'lchov yo'q (`DayCell` da `fill` + `late` + `early`).
 * Ma'nosiz balandlik ko'rinadigan, lekin bo'sh signal bo'lardi.
 */
export function CompactDayStrip({ cells }: { cells: DayCell[] }) {
	const { t } = useTranslation("employees");
	const width = cells.length * (CELL_W + GAP) - GAP;
	const label = describeCells(cells, t);

	return (
		<Tooltip title={label}>
			<svg
				role="img"
				aria-label={label}
				width={width}
				height={HEIGHT}
				viewBox={`0 0 ${width} ${HEIGHT}`}
			>
				{cells.map((cell, index) => (
					// ⚠️ `key` — `cell.date`, indeks EMAS.
					<rect
						key={cell.date}
						x={index * (CELL_W + GAP)}
						y={0}
						width={CELL_W}
						height={HEIGHT}
						rx={1}
						fill={DAY_FILL_COLORS[cell.fill]}
					/>
				))}
			</svg>
		</Tooltip>
	);
}
